import { Route, Navigate } from "react-router-dom";
import Dashboard from "@/pages/Dashboard";
import SalesDashboard from "@/pages/SalesDashboard";
import Leads from "@/pages/Leads";
import Clients from "@/pages/Clients";
import Sites from "@/pages/Sites";
import Equipment from "@/pages/Equipment";
import Proposals from "@/pages/Proposals";
import InspectionConduct from "@/pages/InspectionConduct";
import SettingsPage from "@/pages/Settings";
import DispatchPage from "@/pages/Dispatch";
import MyWorkOrders from "@/pages/MyWorkOrders";
import MapView from "@/pages/MapView";
import WorkOrdersPage from "@/pages/WorkOrdersPage";
import WorkOrderDetailPage from "@/pages/WorkOrderDetailPage";
import ChangeOrders from "@/pages/ChangeOrders";
import TimesheetsPage from "@/pages/Timesheets";
import Purchasing from "@/pages/Purchasing";
import Invoicing from "@/pages/Invoicing";
import Profile from "@/pages/Profile";
import { useAuth } from "@/auth/AuthContext";
import ProtectedRoute from "@/auth/ProtectedRoute";
import { resolveLandingPath } from "@/lib/landingPages";

// Page access is decided ONLY by the access matrix (Settings → Users → Permissions) via each route's `surface`.
export const SETTINGS_SURFACES = ["settings.general", "settings.users", "settings.dispatch", "settings.equipment", "settings.vendors", "settings.inspections", "settings.geocoding", "settings.wiki"];

/** Default landing — respects each user's chosen landing page (Settings → Users). */
function RoleHome() {
  const { user, can } = useAuth();
  const target = resolveLandingPath(user, can);
  if (target !== "/" && target !== "/ops") return <Navigate to={target} replace />;
  if (can("ops.dashboard", "read")) return <Navigate to="/ops" replace />;
  return <Navigate to="/field" replace />;
}

// The signed-in route tree. Rendered once under <Layout> (browser URL) and again inside the split-screen pane (memory router).
export const protectedRoutes = () => [
  <Route key="home" index element={<RoleHome />} />,
  <Route key="ops" path="ops" element={<ProtectedRoute surface="ops.dashboard"><Dashboard /></ProtectedRoute>} />,
  <Route key="sales" path="sales" element={<ProtectedRoute surface="sales.dashboard"><SalesDashboard /></ProtectedRoute>} />,
  <Route key="dashboard" path="dashboard" element={<Navigate to="/ops" replace />} />,
  <Route key="leads" path="leads" element={<ProtectedRoute surface="sales.leads"><Leads /></ProtectedRoute>} />,
  <Route key="clients" path="clients" element={<ProtectedRoute surface="sales.customers"><Clients /></ProtectedRoute>} />,
  <Route key="client" path="clients/:clientId" element={<ProtectedRoute surface="sales.customers"><Clients /></ProtectedRoute>} />,
  <Route key="client-sites" path="clients/:clientId/sites" element={<ProtectedRoute surface="sales.customers"><Sites /></ProtectedRoute>} />,
  <Route key="client-equipment" path="clients/:clientId/equipment" element={<ProtectedRoute surface="sales.customers"><Equipment /></ProtectedRoute>} />,
  <Route key="sites" path="sites" element={<ProtectedRoute surface="sales.customers"><Sites /></ProtectedRoute>} />,
  <Route key="equipment" path="equipment" element={<Navigate to="/settings" state={{ tab: "equipment" }} replace />} />,
  <Route key="inspections" path="inspections" element={<Navigate to="/settings" state={{ tab: "inspections" }} replace />} />,
  <Route key="inspection" path="inspections/:id" element={<ProtectedRoute surface="ops.inspections"><InspectionConduct /></ProtectedRoute>} />,
  <Route key="proposals" path="proposals" element={<ProtectedRoute surface="sales.proposals"><Proposals /></ProtectedRoute>} />,
  <Route key="work-orders" path="work-orders" element={<ProtectedRoute surface="ops.work_orders"><WorkOrdersPage /></ProtectedRoute>} />,
  <Route key="work-order" path="work-orders/:id" element={<ProtectedRoute surface="ops.work_orders"><WorkOrderDetailPage /></ProtectedRoute>} />,
  <Route key="change-orders" path="change-orders" element={<ProtectedRoute surface="ops.work_orders"><ChangeOrders /></ProtectedRoute>} />,
  <Route key="settings" path="settings" element={<ProtectedRoute surfaces={SETTINGS_SURFACES}><SettingsPage /></ProtectedRoute>} />,
  <Route key="dispatch" path="dispatch" element={<ProtectedRoute surface="ops.dispatch"><DispatchPage /></ProtectedRoute>} />,
  <Route key="field" path="field" element={<ProtectedRoute surface="ops.tech"><MyWorkOrders /></ProtectedRoute>} />,
  <Route key="timesheets" path="timesheets" element={<ProtectedRoute surface="ops.timesheets"><TimesheetsPage /></ProtectedRoute>} />,
  <Route key="purchasing" path="purchasing" element={<ProtectedRoute surfaces={["ops.purchasing", "ops.warehouse"]}><Purchasing /></ProtectedRoute>} />,
  <Route key="warehouse" path="warehouse" element={<Navigate to="/purchasing?tab=receiving" replace />} />,
  <Route key="invoicing" path="invoicing" element={<ProtectedRoute surface="ops.invoices"><Invoicing /></ProtectedRoute>} />,
  <Route key="map" path="map" element={<ProtectedRoute surface="ops.map"><MapView /></ProtectedRoute>} />,
  <Route key="profile" path="profile" element={<Profile />} />,
  <Route key="fallback" path="*" element={<Navigate to="/" replace />} />,
];
